/**
 * Design Tokens for MoreMe AI Enterprise Learning Platform
 * Premium Brand Identity - World-Class Design System
 * 
 * These tokens ensure consistency across marketing site and internal application.
 * All components should reference these tokens instead of hardcoded values.
 */

export const designTokens = {
  colors: {
    primary: {
      50: '#EEF2FF',
      100: '#E0E7FF',
      200: '#C7D2FE',
      300: '#A5B4FC',
      400: '#818CF8',
      500: '#2D5BE3', // Main brand color - Trust & Technology
      600: '#5580FF', // Light variant
      700: '#1E3FAF', // Dark variant
      800: '#1E3A8A',
      900: '#1E293B',
      main: '#2D5BE3',
      light: '#5580FF',
      dark: '#1E3FAF',
      contrastText: '#FFFFFF',
    },
    
    secondary: {
      50: '#FAF5FF',
      100: '#F3E8FF',
      200: '#E9D5FF',
      300: '#D8B4FE',
      400: '#C084FC',
      500: '#8B5CF6', // Main accent color - Innovation & Creativity
      600: '#A78BFA', // Light variant
      700: '#6D28D9', // Dark variant
      800: '#5B21B6',
      900: '#4C1D95',
      main: '#8B5CF6',
      light: '#A78BFA',
      dark: '#6D28D9',
      contrastText: '#FFFFFF',
    },
    
    success: {
      50: '#ECFDF5',
      100: '#D1FAE5',
      200: '#A7F3D0',
      300: '#6EE7B7',
      400: '#34D399',
      500: '#10B981', // Growth & Achievement
      600: '#059669',
      700: '#047857',
      800: '#065F46',
      900: '#064E3B',
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
      contrastText: '#FFFFFF',
    },
    
    warning: {
      50: '#FFFBEB',
      100: '#FEF3C7',
      200: '#FDE68A',
      300: '#FCD34D',
      400: '#FBBF24',
      500: '#F59E0B', // Attention & Gamification
      600: '#D97706',
      700: '#B45309',
      800: '#92400E',
      900: '#78350F',
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#B45309',
      contrastText: '#111827',
    },
    
    error: {
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      300: '#FCA5A5',
      400: '#F87171',
      500: '#EF4444', // Critical States
      600: '#DC2626',
      700: '#B91C1C',
      800: '#991B1B',
      900: '#7F1D1D',
      main: '#EF4444',
      light: '#F87171',
      dark: '#B91C1C',
      contrastText: '#FFFFFF',
    },
    
    info: {
      50: '#ECFEFF',
      100: '#CFFAFE',
      200: '#A5F3FC',
      300: '#67E8F9',
      400: '#22D3EE',
      500: '#06B6D4', // Information & Insights
      600: '#0891B2',
      700: '#0E7490',
      800: '#155E75',
      900: '#164E63',
      main: '#06B6D4',
      light: '#67E8F9',
      dark: '#0E7490',
      contrastText: '#0F172A',
    },
    
    neutral: {
      50: '#F8FAFC',
      100: '#F1F5F9',
      200: '#E2E8F0',
      300: '#CBD5E1',
      400: '#94A3B8',
      500: '#64748B', // Main neutral color
      600: '#475569',
      700: '#334155',
      800: '#1E293B',
      900: '#0F172A',
    },
    
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
      subtle: '#F8FAFC',
      muted: '#F1F5F9',
      elevated: '#FFFFFF',
    },
    
    text: {
      primary: '#0F172A',
      secondary: '#475569',
      disabled: '#94A3B8',
      inverse: '#FFFFFF',
    },
    
    border: {
      light: '#F1F5F9',
      main: '#E2E8F0',
      dark: '#CBD5E1',
    },
    
    gradients: {
      primary: 'linear-gradient(135deg, #4B88FF 0%, #2563EB 100%)',
      subtle: 'linear-gradient(135deg, #F3F6F9 0%, #EFF6FF 100%)',
      success: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
      hero: 'linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)',
      profile: 'linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)',
    },
    
    sidebar: {
      background: '#FFFFFF',
      backgroundHover: '#F3F6F9',
      text: '#1A1D1F',
      textMuted: '#9A9FA5',
      activeBackground: 'rgba(75, 136, 255, 0.1)',
      activeBorder: '#4B88FF',
      hoverBackground: '#F3F6F9',
      iconColor: '#9A9FA5',
      iconActiveColor: '#4B88FF',
    },
    
    card: {
      background: '#FFFFFF',
      borderRadius: '24px',
      shadow: '0 10px 40px rgba(0, 0, 0, 0.04)',
      shadowHover: '0 15px 50px rgba(0, 0, 0, 0.08)',
    },
    
    accent: {
      lime: '#C0EB38',
      limeLight: '#D4F248',
      limeDark: '#A8D42F',
      blue: '#4B88FF',
      blueLight: '#60A5FA',
      blueDark: '#3B82F6',
    },
    
    mockup: {
      limeGreen: '#C0EB38',
      limeGreenHover: '#A8D42F',
      blueGradient: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
      cardRadius: '32px',
      cardShadow: '0 10px 40px rgba(0, 0, 0, 0.06)',
      backgroundGradient: 'linear-gradient(180deg, #E8F4FD 0%, #F0F4F8 50%, #FFFFFF 100%)',
      navRailBg: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
      navRailWidth: '80px',
    },
  },
  
  typography: {
    fontFamily: {
      primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      mono: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
    },
    
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
      '6xl': '3.75rem', // 60px
    },
    
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
      loose: 2,
    },
    
    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em',
    },
  },
  
  spacing: {
    0: '0',
    1: '0.25rem',  // 4px
    2: '0.5rem',   // 8px
    3: '0.75rem',  // 12px
    4: '1rem',     // 16px
    5: '1.25rem',  // 20px
    6: '1.5rem',   // 24px
    7: '1.75rem',  // 28px
    8: '2rem',     // 32px
    10: '2.5rem',  // 40px
    12: '3rem',    // 48px
    16: '4rem',    // 64px
    20: '5rem',    // 80px
    24: '6rem',    // 96px
    32: '8rem',    // 128px
  },
  
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    base: '0.5rem',  // 8px
    md: '0.75rem',   // 12px
    lg: '1rem',      // 16px
    xl: '1.5rem',    // 24px
    '2xl': '2rem',   // 32px
    full: '9999px',
  },
  
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none',
  },
  
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    slower: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  
  breakpoints: {
    xs: '0px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  zIndex: {
    hide: -1,
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    modalBackdrop: 1300,
    modal: 1400,
    popover: 1500,
    tooltip: 1600,
  },
  
  iconSize: {
    xs: 12,
    sm: 16,
    base: 20,
    lg: 24,
    xl: 32,
    '2xl': 48,
  },
  
  layout: {
    maxWidth: '1280px',
    contentPadding: {
      mobile: '1rem',    // 16px
      tablet: '1.5rem',  // 24px
      desktop: '2rem',   // 32px
    },
  },
};

export const cssVariables = `
  :root {
    /* Primary Colors */
    --color-primary-50: ${designTokens.colors.primary[50]};
    --color-primary-500: ${designTokens.colors.primary[500]};
    --color-primary-900: ${designTokens.colors.primary[900]};
    
    /* Secondary Colors */
    --color-secondary-50: ${designTokens.colors.secondary[50]};
    --color-secondary-500: ${designTokens.colors.secondary[500]};
    --color-secondary-900: ${designTokens.colors.secondary[900]};
    
    /* Neutral Colors */
    --color-neutral-50: ${designTokens.colors.neutral[50]};
    --color-neutral-500: ${designTokens.colors.neutral[500]};
    --color-neutral-900: ${designTokens.colors.neutral[900]};
    
    /* Semantic Colors */
    --color-success: ${designTokens.colors.success.main};
    --color-warning: ${designTokens.colors.warning.main};
    --color-error: ${designTokens.colors.error.main};
    --color-info: ${designTokens.colors.info.main};
    
    /* Background */
    --color-bg-default: ${designTokens.colors.background.default};
    --color-bg-subtle: ${designTokens.colors.background.subtle};
    --color-bg-muted: ${designTokens.colors.background.muted};
    
    /* Text */
    --color-text-primary: ${designTokens.colors.text.primary};
    --color-text-secondary: ${designTokens.colors.text.secondary};
    --color-text-disabled: ${designTokens.colors.text.disabled};
    
    /* Border */
    --color-border-light: ${designTokens.colors.border.light};
    --color-border-main: ${designTokens.colors.border.main};
    --color-border-dark: ${designTokens.colors.border.dark};
    
    /* Typography */
    --font-family-primary: ${designTokens.typography.fontFamily.primary};
    --font-family-mono: ${designTokens.typography.fontFamily.mono};
    
    /* Spacing */
    --spacing-2: ${designTokens.spacing[2]};
    --spacing-4: ${designTokens.spacing[4]};
    --spacing-6: ${designTokens.spacing[6]};
    --spacing-8: ${designTokens.spacing[8]};
    
    /* Border Radius */
    --radius-sm: ${designTokens.borderRadius.sm};
    --radius-base: ${designTokens.borderRadius.base};
    --radius-lg: ${designTokens.borderRadius.lg};
    
    /* Shadows */
    --shadow-sm: ${designTokens.shadows.sm};
    --shadow-base: ${designTokens.shadows.base};
    --shadow-lg: ${designTokens.shadows.lg};
    
    /* Transitions */
    --transition-fast: ${designTokens.transitions.fast};
    --transition-base: ${designTokens.transitions.base};
  }
`;

export default designTokens;
